import React from 'react';
import { trackProblemsCTAClick } from '../../mixpanel/events';

const ProblemsSection = () => {
  const handleCTAClick = () => {
    trackProblemsCTAClick();
  };

  return (
    <section className="bg-apple-gray-100 text-apple-gray-800 py-20 px-4" id="problems">
      <div className="max-w-4xl mx-auto text-center">
        {/* Main Headline */}
        <h2 className="text-3xl md:text-4xl font-bold text-apple-gray-800 mb-6 leading-tight">
          Are you a non-technical founder building a software product?
        </h2>

        {/* Subheadline */}
        <h3 className="text-xl md:text-2xl font-semibold text-apple-gray-600 mb-8">
          Currently working with a software agency and nothing works?
        </h3>

        {/* Problem Points */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Missed Deadlines</h4>
              </div>
              <p className="text-apple-gray-600">Your agency consistently delivers late, pushing back your product launch and burning through your runway.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Budget Overruns</h4>
              </div>
              <p className="text-apple-gray-600">Costs keep escalating with no clear explanation, eating into your funding faster than expected.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Poor Communication</h4>
              </div>
              <p className="text-apple-gray-600">You're left in the dark about progress, with unclear updates and no visibility into what's actually being built.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Quality Issues</h4>
              </div>
              <p className="text-apple-gray-600">The delivered product is buggy, doesn't match requirements, or lacks the scalability your SaaS needs.</p>
            </div>
          </div>
        </div>

        {/* Solution Statement */}
        <div className="mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-apple-gray-800 mb-4">
            Leaving Them Will Not Solve The Problem. You Need a New Approach.
          </h3>
          <p className="text-lg text-apple-gray-600 max-w-2xl mx-auto">
            I work with non-technical founders like you to fix chaotic builds, align agencies, and finally get working software without having to start from scratch.
          </p>
        </div>

        {/* Call to Action */}
        <div className="bg-apple-gray-800 text-white p-8 rounded-lg">
          <h4 className="text-xl font-semibold mb-4">How much more cash will you burn by waiting another week?</h4>
          <p className="text-apple-gray-200 mb-6">
            Let's discuss your specific challenges and create a plan to get you back on track.
          </p>
          <div className="text-center">
            <a
              href="https://form.typeform.com/to/SLoI0dkA"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCTAClick}
              className="inline-block bg-white text-apple-gray-800 font-semibold py-4 px-8 rounded-lg hover:bg-apple-gray-100 transition-colors duration-200 text-lg w-full md:w-auto"
            >
              Talk to an Expert
            </a>
            <p className="text-apple-gray-300 text-sm mt-3">
              Free 30-min audit to understand your pain points.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemsSection;